"use client";

import React, { useState, useEffect } from "react";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useForm } from "react-hook-form";
import FormDatePicker from "../components/FormDatePicker";
import { axiosPostMultipart } from "../utils/axios";
import { getUserId } from "../utils/session";
import Notification from "../components/Notification";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import useSWR from "swr";
import fetcher from "../utils/fetcher";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Box } from "@mui/material";
dayjs.extend(utc);

function PublicKey() {
  const [uploadedFile, setUploadedFile] = useState(null);
  const { register, control, errors, watch, handleSubmit, setError, clearErrors } = useForm();
  const [status, setStatus] = useState({});
  const userId = getUserId();
  const {
    data: userData,
    error,
    isLoading,
    setValue,
  } = useSWR(`${process.env.REACT_APP_ACCESS_MGMT_URL}/users/${userId}`, fetcher);

  useEffect(() => {
    setUploadedFile(watch("file"));
  }, [watch, userData, setValue]);

  // Format date to MM/DD/YYYY
  const formatDate = (date) => {
    return dayjs.utc(date).format("MM/DD/YYYY");
  };

  const onSubmit = handleSubmit(async (inputs) => {
    if (!inputs.file || !inputs.file[0]) {
      setStatus({
        state: "error",
        message: "Please upload a file",
      });
      return;
    }
    const form = new FormData();
    form.append("activatedAt", dayjs.utc(inputs.activatedAt));
    form.append("deActivateAt", dayjs.utc(inputs.deActivateAt));
    form.append("File", inputs.file[0]);
    if (!inputs.file[0].name.includes(".asc")) {
      setStatus({
        state: "error",
        message: "Upload a valid .asc file",
      });
    } else {
      axiosPostMultipart(
        `${process.env.REACT_APP_ACCESS_MGMT_URL}/users/${userId}/pgpPublicKeys`,
        form,
      )
        .then(({ data }) => {
          console.log(data);
          setStatus({
            state: "success",
            message: "PGP public key uploaded successfully",
          });
        })
        .catch((error) => {
          console.log(error);
          setStatus({
            state: "error",
            message: "Unable to upload PGP Public Key",
          });
        });
    }
  });
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", p: 3 }}>
      <Paper
        sx={{
          mb: 5,
          ml: "auto",
          display: "flex",
          flexWrap: "wrap",
          gap: 3,
          alignItems: "end",
          justifyContent: "start",
        }}
        elevation={6}
      >
        <p
          style={{
            fontSize: "1.25rem",
            marginLeft: "2rem",
            marginBottom: "1rem",
            marginTop: "1rem",
          }}
        >
          Update PGP Public Key File
        </p>
      </Paper>
      <Paper
        sx={{
          mb: 5,
          ml: "auto",
          display: "flex",
          flexWrap: "wrap",
          gap: 3,
          alignItems: "end",
          justifyContent: "start",
        }}
        elevation={6}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ display: "flex", gap: 12, p: 2, alignItems: "center" }}>
            <Box>Member Organization(s):</Box>
            <Box>
              {userData?.organizations?.length === 0 && (
                <li
                  style={{
                    listStyle: "disc",
                    gap: 12,
                    padding: 4,
                    marginLeft: 64,
                  }}
                >
                  You are not a member of any organizations
                </li>
              )}
              {userData?.organizations?.length > 0 &&
                userData?.organizations?.map((org) => (
                  <li
                    style={{
                      listStyle: "disc",
                      gap: 12,
                      padding: 4,
                      marginLeft: 64,
                    }}
                    key={org.organizationId}
                  >
                    {org.organizationName}
                  </li>
                ))}
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 12, p: 2, alignItems: "center" }}>
            <Box sx={{ p: 1, width: 256 }}>File uploaded status:</Box>
            <Box>
              {userData?.isPgpPublicKey ? (
                <CheckCircleIcon color="success" />
              ) : (
                <>
                  <CancelIcon color="error" />
                  You must upload a valid PGP Public Key file before you can be
                  selected as a Code File recipient.
                </>
              )}
            </Box>
          </Box>
          <Box>
            <input
              style={{ display: "none" }}
              id="raised-button-file"
              multiple
              accept="*.asc"
              type="file"
              {...register("file")}
            />
            <label htmlFor="raised-button-file">
              <Box sx={{ display: "flex", gap: 12, p: 2, alignItems: "center" }}>
                <span style={{ padding: 4, width: 256 }}>Public Key File*</span>
                <Button
                  sx={{ width: 384 }}
                  color="info"
                  variant="contained"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                >
                  Upload
                </Button>
                {!uploadedFile && (
                  <span style={{ color: "#ef4444" }}>File is required</span>
                )}
              </Box>
            </label>
          </Box>
          <Box sx={{ display: "flex", gap: 12, p: 2, alignItems: "center" }}>
            <span style={{ padding: 4, width: 256 }}>Active On*</span>
            <FormDatePicker
              rules={{ required: { value: true, message: "This is a required field" } }}
              name="activatedAt"
              control={control}
              disablePast
              sx={{ width: 384 }}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 12, p: 2, alignItems: "center" }}>
            <span style={{ padding: 4, width: 256 }}>Inactive On*</span>
            <FormDatePicker
              rules={{ required: { value: true, message: "This is a required field" } }}
              name="deActivateAt"
              control={control}
              disablePast
              sx={{ width: 384 }}
            />
          </Box>
          <Button
            sx={{ ml: 8, mt: 10, mb: 5, width: 384 }}
            color="secondary"
            variant="contained"
            onClick={onSubmit}
          >
            {userData?.isPgpPublicKey ? "Update" : "Save"}
          </Button>
        </form>
      </Paper>
      {status.state && (
        <Notification
          message={status.message}
          type={status.state}
          open={true}
          handleClose={() => setStatus({})}
        />
      )}
    </Box>
  );
}

export default PublicKey;
